import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { ProgressBar } from '@/components/ui/progress-bar';

interface PlanTimeTrackerProps {
  /** Sum of the sections' durations. */
  scheduledMinutes: number;
  /** The plan's overall duration from "1. Plan information". */
  planMinutes: number;
}

// Sticky offset under the app's sticky TopNav — same value the groups page
// uses for its selection toolbar (group-members-panel.tsx).
const TOP_NAV_OFFSET = '76px';

// "2. Plan content" header for the builder: how much of the plan's duration
// the sections have claimed so far and what is left to allocate. Sticky so
// the running total stays in view while a coach scrolls through and adds
// sections. Pure presentation — the numbers are derived in builder-screen.
export default function PlanTimeTracker({ scheduledMinutes, planMinutes }: PlanTimeTrackerProps) {
  const hasPlanDuration = planMinutes > 0;
  const remaining = planMinutes - scheduledMinutes;
  const pct = hasPlanDuration ? (scheduledMinutes / planMinutes) * 100 : 0;

  // Brand while there is still time to hand out (the prototype's default),
  // green once the sections exactly fill the plan, red when they overrun it.
  let badge: { label: string; variant: 'info' | 'success' | 'danger'; fill?: string } | null = null;
  if (hasPlanDuration) {
    if (remaining > 0) badge = { label: `${remaining} min left`, variant: 'info' };
    else if (remaining === 0) badge = { label: 'Fully scheduled', variant: 'success', fill: 'success' };
    else badge = { label: `${-remaining} min over`, variant: 'danger', fill: 'danger' };
  }

  return (
    <Box position='sticky' top={TOP_NAV_OFFSET} zIndex={5} bg='bg.panel' pb={3} mb={1}>
      <Flex align='center' justify='space-between' gap={3} wrap='wrap' mb={2}>
        <Text textStyle='eyebrow'>2. Plan content</Text>
        <Flex align='center' gap={2} wrap='wrap'>
          <Text fontSize='xs' color='fg.muted'>
            {hasPlanDuration
              ? `${scheduledMinutes} min scheduled of ${planMinutes} min plan`
              : `${scheduledMinutes} min scheduled — set a plan duration to track time left`}
          </Text>
          {badge && (
            <Badge size='sm' variant={badge.variant}>
              {badge.label}
            </Badge>
          )}
        </Flex>
      </Flex>
      <ProgressBar value={pct} fill={badge?.fill} thickness='4px' />
    </Box>
  );
}
